import { Box, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import NewContract from '../../components/new/NewContract';
import ExoTable from '../../components/exo/ExoTable';
import { ContractStatus } from '../../components/special/Status';
import ExtendContract from './ExtendContract';
import Header from '../../components/special/Header';
import ExoTableRecipient from '../../components/exo/ExoTableRecipient';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../components/label/MoneyLabel';
import { useNavigate } from 'react-router-dom';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import useFetch from '../../api/useFetch';
import { recipientTypeMap } from '../../components/routing/routingMaps';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import NewRecurringInvoice from '../../components/new/NewRecurringInvoice';
import { LeadStatusSelector } from '../leads/status/LeadStatusSelector';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import ExoAlert from '../../components/exo/ExoAlert';
import DocumentOverviewCard from '../../components/document-overview/DocumentOverviewCard';
import { buildPermanentFilter } from '../../components/document-overview/buildPermanentFilter';

const Contracts = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  // null until the overview card reports its (persisted) filter — prevents a
  // double table fetch (unfiltered then filtered) on initial load.
  const [overviewFilters, setOverviewFilters] = useState(null);
  const permanentFilter = overviewFilters
    ? buildPermanentFilter({ ...overviewFilters, dateField: 'contractStartDate' })
    : '';
  const fetchPath =
    overviewFilters === null
      ? null
      : permanentFilter
        ? `contracts?${permanentFilter}`
        : 'contracts';

  const { apiData, setApiData, isLoading } = useFetch(fetchPath);

  const config = useConfig();

  const alert = {
    type: 'warning',
    message: t(
      'Are you sure you want to change the contract information? A change in the contract requires the renewed consent of the other party. The modified contract will be automatically sent to the other party by mail.',
    ),
    submitLabel: t('Yes Make Change'),
  };

  const [alertData, setAlertData] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleStatusClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    if (status.status == 4) {
      submitStatusChange({ status: status.status, row: item });
    } else {
      setAlertData({ status: status.status, row: item });
    }
  }

  function submitStatusChange(data) {
    DBRequest({
      config,
      path: `contracts/${data.row.id}`,
      method: API_PATCH({ ...data.row, status: data.status }),
      onResponse: () => {
        setApiData({
          ...apiData,
          data: updateOrCreate(apiData.data, {
            ...data.row,
            status: data.status,
          }),
        });
        setAlertData(null);
      },
    });
  }

  // dialog
  const [open, setOpen] = useState(false);
  const handleClick = (event, reason) => {
    if (reason && reason == 'backdropClick') return;
    setOpen(!open);
  };

  // table
  const Columns = [
    {
      header: t('Nr'), // header of column
      key: 'nr', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="monospace whitespace-nowrap">{row.nr}</Box>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Status'),
      key: 'status',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <ContractStatus
          number={row.status}
          onClick={(e) => handleStatusClick(e, row)}
        ></ContractStatus>
      ), // custom xml for cells in the column. you get the row as object
    },

    {
      header: t('Title'),
      hiddenMobile: true,
      key: 'title',
      width: '100%',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      onClick: (row) => navigate(`/contracts/${row.id}`),
    },
    {
      header: t('Recipient'),
      key: 'recipient',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      className: 'w-full md:min-w-[270px]',
      cell: ({ row }) => <ExoTableRecipient row={row} />,
      onClick: (row) =>
        navigate(`/${recipientTypeMap[row.recipientType]}/${row.recipient.id}`),
    },
    {
      header: t('Rhythm'),
      key: 'rhythm',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      width: '150px',
      hiddenMobile: true,
      cell: ({ row }) => <Box>{getRhythm(row.paymentRhythm)}</Box>,
      sortable: true, // enable sort function for column
    },
    {
      header: t('netto'),
      key: 'price',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.price} />,
      sortable: true, // enable sort function for column
    },
  ];
  function getRhythm(number) {
    switch (parseInt(number)) {
      case 1:
        return t('Monthly');
      case 2:
        return t('Every Two Months');
      case 3:
        return t('Quarterly');
      case 6:
        return t('Semiannual');
      case 12:
        return t('Annual');
      default:
        return number;
    }
  }

  const filters = {
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'dropdown',
          label: t('Status'),
          key: 'state2',
          baseValue: 'status[eq]=',
          active: false,
          options: [
            {
              label: t('Created'),
              value: 3,
            },
            {
              label: t('Open'),
              value: 0,
            },
            {
              label: t('Accepted'),
              value: 1,
            },
            {
              label: t('Rejected'),
              value: 2,
            },
            { label: t('Finished'), value: 4 },
          ],
        },
      ],
    },
  };

  return (
    <>
      <Header
        title={t('Subscriptions')}
        subtitle="Here you can find all contracts and easily set up new ones."
      />

      <DocumentOverviewCard
        resource="contracts"
        preferencesKey="contracts"
        onFilterChange={setOverviewFilters}
        actions={[
          {
            primary: true,
            label: t('New Subscription'),
            icon: <NoteAddIcon fontSize="small" />,
            onClick: handleClick,
          },
          {
            label: t('New Preset'),
            icon: <HistoryEduIcon fontSize="small" />,
            onClick: () => navigate('/contract-presets'),
          },
        ]}
      />

      <ExoTable
        data={apiData}
        isLoading={isLoading}
        columns={Columns}
        extendElement={({ row, onUpdate }) => (
          <ExtendContract row={row} onUpdate={onUpdate} />
        )}
        filter={permanentFilter}
        filters={filters}
      />
      <NewContract
        open={open}
        setContracts={setApiData}
        contracts={apiData}
        onClose={handleClick}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
        customStatuses={[
          {
            label: t('Created'),
            status: 3,
            variant: 'draft',
          },
          {
            label: t('Sent'),
            status: 0,
            variant: 'sent',
          },
          {
            label: t('Accepted'),
            status: 1,
            variant: 'accepted',
          },
          {
            label: t('Rejected'),
            status: 2,
            variant: 'rejected',
          },
          { label: t('Finished'), status: 4, variant: 'finished' },
        ]}
      />
      <ExoAlert
        open={alertData}
        onSubmit={() => submitStatusChange(alertData)}
        onClose={() => setAlertData(null)}
        alert={alert}
      />
    </>
  );
};

export default Contracts;
